import { Box, Tab, Tabs, tabsClasses } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { capitalCase } from 'change-case';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL } from 'src/services/urlService';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import Facebook_Icon from '../../assets/facebook.svg';
import Gbusiness_Icon from '../../assets/google-my-business-logo.svg';
import Instagram_Icon from '../../assets/instagram.svg';
import LinkedIn_Icon from '../../assets/linked_in.svg';
import Pinterest_Icon from '../../assets/pinterest-icon.svg';
import TikTok_Icon from '../../assets/tiktok.svg';
import Threads_Icon from '../../assets/threads.svg';
import Youtube_Icon from '../../assets/youtube.png';
import TwitterIcon from '../../assets/twitter.svg';
import BlueskyIcon from '../../assets/bluesky.svg';
import SnapChatIcon from '../../assets/snapchat.svg';

import CommonReAuthenticateComponent from '../integration/commonReAuthenticate';
import { PostUtil } from '../new-post-new/post-util';
import FacebookPostGrid from './facebookPosts/facebookPostGrid.component';
import GoogleMyBusinessPostGrid from './googlemybusinessPosts/googlemybusinessPostGrid.component';
import InstagramPostGrid from './instagramPosts/instagramPostGrid.component';
import LinkedInPagePostGrid from './linkedInPagePosts/linkedInPostGrid.component';
import PinterestPostGrid from './pinterestPosts/pinterestPostGrid.component';
import TikTokPostGrid from './tiktokPosts/tiktokPostGrid.component';
import YoutubePostGrid from './youtubePosts/youtubePostGrid.component';
import ThreadsPostGrid from './threadsPosts/threadsPostGrid.component';
import TwitterPostGrid from './twitterPosts/twitterPostGrid.component';
import BlueskyPostGrid from './blueskyPosts/blueskyPostGrid.component';
import SnapchatPostGrid from './snapchatPosts/snapchatPostGrid.component';

import { CommonHelper } from 'src/utils/commonHelper';

const PublishedPostComponent = () => {
  const { t: translate } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { checkAndTriggerReAuth } = useReAuth();
  const isShowOfferBanner = CommonHelper.ShowOfferBanner();

  // State Variables
  const [currentTab, setCurrentTab] = useState(translate('Facebook Page'));
  const [facebookNetworks, setFacebookNetworks] = useState([]);
  const [facebookGroupNetworks, setfacebookGroupNetworks] = useState([]);
  const [instagramNetworks, setInstagramNetworks] = useState([]);
  const [youtubeNetworks, setYoutubeNetworks] = useState([]);
  const [twitterNetworks, setTwitterNetworks] = useState([]);
  const [linkedInNetworks, setLinkedInNetworks] = useState([]);
  const [tiktokNetworks, setTiktokNetworks] = useState([]);
  const [gbusinessNetworks, setGBusinessNetworks] = useState([]);
  const [pinterestNetworks, setPinterestNetworks] = useState([]);
  const [threadsNetworks, setThreadsNetworks] = useState([]);
  const [blueSkyNetworks, setBlueSkyNetworks] = useState([]);
  const [snapchatNetworks, setSnapchatNetworks] = useState([]);        // ← New
  const [isNetworksLoaded, setNetworkLoaded] = useState(false);
  const [selectedPopOverAccount, setSelectedPopOverAccount] = useState();
  const [reAuthenticateOpen, setReAuthenticateOpen] = useState(false);

  useEffect(() => {
    getConnectedNetworks();
  }, []);

  const connectedNetworksSuccessCallback = (data, message) => {
    const sortByActive = (arr) => [...arr].sort((a, b) => Number(b.is_active) - Number(a.is_active));

    let fNetworks = sortByActive(
      data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.FACEBOOK && 
                       a.profile_type === PostUtil.ProfileType.PAGE && a.is_enabled)
    );

    let fgNetworks = sortByActive(
      data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.FACEBOOK && 
                       a.profile_type === PostUtil.ProfileType.GROUP && a.is_enabled)
    );

    let iNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.INSTAGRAM && a.is_enabled));
    let yNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.YOUTUBE && a.is_enabled));
    let tNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.TWITTER && a.is_enabled));
    let gNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.GBUSINESS && a.is_enabled));
    let pNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.PINTEREST && a.is_enabled));
    let tiNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.TIKTOK && a.is_enabled));
    let lNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.LINKEDIN && a.profile_type === 'PAGE' && a.is_enabled));
    let thNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.THREADS && a.is_enabled));
    let bNetworks = sortByActive(data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.BLUESKY && a.is_enabled));

    // ← Snapchat Added
    let snapNetworks = sortByActive(
      data.filter(a => a.service_name.toUpperCase() === PostUtil.ServiceName.SNAPCHAT && a.is_enabled)
    );

    checkAndTriggerReAuth(data);

    setFacebookNetworks(fNetworks);
    setfacebookGroupNetworks(fgNetworks);
    setInstagramNetworks(iNetworks);
    setYoutubeNetworks(yNetworks);
    setTwitterNetworks(tNetworks);
    setLinkedInNetworks(lNetworks);
    setTiktokNetworks(tiNetworks);
    setGBusinessNetworks(gNetworks);
    setPinterestNetworks(pNetworks);
    setThreadsNetworks(thNetworks);
    setBlueSkyNetworks(bNetworks);
    setSnapchatNetworks(snapNetworks);        // ← New
    setNetworkLoaded(true);
  };

  const connectedNetworkFailureCallback = (message) => {
    setFacebookNetworks([]);
    setInstagramNetworks([]);
    setYoutubeNetworks([]);
    setTwitterNetworks([]);
    setLinkedInNetworks([]);
    setTiktokNetworks([]);
    setGBusinessNetworks([]);
    setPinterestNetworks([]);
    setThreadsNetworks([]);
    setBlueSkyNetworks([]);
    setSnapchatNetworks([]);   // ← New
  };

  const getConnectedNetworks = () => {
    let getProps = {
      url: integrateModuleURL.getConnectedNetworks,
      successCallback: connectedNetworksSuccessCallback,
      failureCallback: connectedNetworkFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const handleCloseReauthenticate = () => {
    setReAuthenticateOpen(false);
    setSelectedPopOverAccount(null);
  };

  const handleProfileClick = (profile) => {
    setSelectedPopOverAccount(profile);
    setReAuthenticateOpen(true);
  };

  const ACCOUNT_TABS = [
    { value: 'Facebook Page', label: translate('social_inbox.facebook_page'), icon: <img src={Facebook_Icon} width={28} height={20} />, component: <FacebookPostGrid connectedNetworks={facebookNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'Instagram', label: translate('social_inbox.instagram'), icon: <img src={Instagram_Icon} width={28} height={20} />, component: <InstagramPostGrid connectedNetworks={instagramNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'Youtube', label: translate('report.youtube'), icon: <img src={Youtube_Icon} width={35} height={35} />, component: <YoutubePostGrid connectedNetworks={youtubeNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'LinkedIn Page', label: translate('published_post.linkedin_page'), icon: <img src={LinkedIn_Icon} width={28} height={20} />, component: <LinkedInPagePostGrid connectedNetworks={linkedInNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'Pinterest', label: translate('published_post.pinterest'), icon: <img src={Pinterest_Icon} width={28} height={20} />, component: <PinterestPostGrid connectedNetworks={pinterestNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'GoogleMyBusiness', label: translate('published_post.g_business'), icon: <img src={Gbusiness_Icon} width={28} height={20} />, component: <GoogleMyBusinessPostGrid connectedNetworks={gbusinessNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'TikTok', label: translate('published_post.tiktok'), icon: <img src={TikTok_Icon} width={28} height={20} />, component: <TikTokPostGrid connectedNetworks={tiktokNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'Threads', label: translate('published_post.threads'), icon: <img src={Threads_Icon} width={28} height={20} />, component: <ThreadsPostGrid connectedNetworks={threadsNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'Twitter', label: translate('published_post.twitter'), icon: <img src={TwitterIcon} width={28} height={20} />, component: <TwitterPostGrid connectedNetworks={twitterNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    { value: 'BlueSky', label: translate('bluesky_published_post.bluesky'), icon: <img src={BlueskyIcon} width={28} height={20} />, component: <BlueskyPostGrid connectedNetworks={blueSkyNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> },
    
    // ← Snapchat Added
    { 
      value: 'Snapchat', 
      label: translate('snapchat_published_post.snapchat') || 'Snapchat', 
      icon: <img src={SnapChatIcon} width={28} height={28} />, 
      component: <SnapchatPostGrid connectedNetworks={snapchatNetworks} handleProfileClick={handleProfileClick} isShowOfferBanner={isShowOfferBanner} /> 
    },
  ];

  return (
    <>
      <Helmet>
        <title>{translate('navbar.menu.published_post')}</title>
      </Helmet>

      <Box>
        <Tabs
          value={currentTab}
          variant="scrollable"
          sx={{
            [`& .${tabsClasses.scrollButtons}`]: { '&.Mui-disabled': { display: 'none' } },
            ...(!isMobile && { gap: '0.5rem', minHeight: '37px' }),
          }}
          allowScrollButtonsMobile
          onChange={(e, value) => setCurrentTab(value)}
        >
          {ACCOUNT_TABS.map((tab) => (
            <Tab
              disableRipple
              key={tab.value}
              label={isMobile ? '' : tab.label}
              icon={tab.icon}
              value={tab.value}
              sx={{
                ...(!isMobile && {
                  flexDirection: 'row',
                  gap: '0.25rem',
                  padding: 0,
                  minHeight: '10px',
                  mr: '0.75rem',
                  fontWeight: 600,
                }),
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }} />

      {isNetworksLoaded &&
        ACCOUNT_TABS.map((tab) => {
          const isMatched = tab.value === currentTab;
          return isMatched && <Box key={tab.value}>{tab.component}</Box>;
        })}

      {reAuthenticateOpen && (
        <CommonReAuthenticateComponent
          selectedPopOverAccount={selectedPopOverAccount}
          getConnectedNetworks={getConnectedNetworks}
          onClose={handleCloseReauthenticate}
        />
      )}
    </>
  );
};

export default PublishedPostComponent;